import React, { useState, useEffect, useCallback } from "react";
import { PAGE_SIZE } from "../../../api";
import { fetchTicketStats, fetchTickets as fetchTicketsApi } from "../services/TicketsService";
import Icon from "../../../components/Icon";
import Tooltip from "../../../components/Tooltip";
import { StatusBadge, PriorityBadge } from "../../../components/Badge";
import Skeleton from "../../../components/Skeleton";
import { EmptyState, ErrorState } from "../../../components/State";
import Pagination from "../../../components/Pagination";
import TicketDetailModal from "../../../components/TicketDetailModal";

export default function TicketsView() {
  const [tickets, setTickets] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [page, setPage] = useState(1);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hasTickets, setHasTickets] = useState(true);
  
  const [filters, setFilters] = useState({
    search: "",
    status: "open",
    priority: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    status: "open",
    priority: "",
  });

  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    in_progress: 0,
    resolved: 0,
    critical: 0,
  });

  const fetchStats = useCallback(async () => {
    try {
      const data = await fetchTicketStats();
      if (data) {
        setStats(data);
      }
    } catch (e) {
      console.error("Could not load ticket stats:", e);
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const data = await fetchTicketsApi({
        limit: PAGE_SIZE,
        offset: offset,
        search: appliedFilters.search,
        status: appliedFilters.status,
        priority: appliedFilters.priority,
      });
      const items = data?.items || [];
      const total = data?.total || 0;
      setTickets(items);
      setTotalCount(total);
      if (!appliedFilters.search && !appliedFilters.status && !appliedFilters.priority) {
        setHasTickets(total > 0 || items.length > 0);
      }
    } catch {
      setError("Could not load tickets.");
    } finally {
      setLoading(false);
    }
  }, [page, appliedFilters]);

  // Sync tickets with page/filters
  useEffect(() => {
    fetchTickets();
  }, [page, appliedFilters, fetchTickets]);

  // Fetch stats on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Debounce search filter input changes for premium instant-feel filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      if (appliedFilters.search !== filters.search) {
        setAppliedFilters((prev) => ({ ...prev, search: filters.search }));
        setPage(1);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [filters.search, appliedFilters.search]);

  // Update status immediately on select change
  const handleStatusChange = (val) => {
    setFilters((prev) => ({ ...prev, status: val }));
    setAppliedFilters((prev) => ({ ...prev, status: val }));
    setPage(1);
  };

  // Update priority immediately on select change
  const handlePriorityChange = (val) => {
    setFilters((prev) => ({ ...prev, priority: val }));
    setAppliedFilters((prev) => ({ ...prev, priority: val }));
    setPage(1);
  };

  function handleSearchSubmit(e) {
    e.preventDefault();
    setAppliedFilters({ ...filters });
    setPage(1);
  }

  function clearFilters() {
    const empty = { search: "", status: "", priority: "" };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  }

  const hasActiveFilter =
    appliedFilters.search || appliedFilters.status || appliedFilters.priority;

  const paged = tickets;

  let activeCardLabel = "";
  if (appliedFilters.status === "open" && !appliedFilters.priority) {
    activeCardLabel = "Open";
  } else if (appliedFilters.status === "in_progress" && !appliedFilters.priority) {
    activeCardLabel = "In Progress";
  } else if (appliedFilters.status === "resolved" && !appliedFilters.priority) {
    activeCardLabel = "Resolved";
  } else if (!appliedFilters.status && !appliedFilters.priority) {
    activeCardLabel = "Total";
  }

  const handleCardClick = (label) => {
    const isActive = activeCardLabel === label;
    if (isActive || label === "Total") {
      setFilters((prev) => ({ ...prev, status: "", priority: "" }));
      setAppliedFilters((prev) => ({ ...prev, status: "", priority: "" }));
    } else if (label === "Open") {
      setFilters((prev) => ({ ...prev, status: "open", priority: "" }));
      setAppliedFilters((prev) => ({ ...prev, status: "open", priority: "" }));
    } else if (label === "In Progress") {
      setFilters((prev) => ({ ...prev, status: "in_progress", priority: "" }));
      setAppliedFilters((prev) => ({ ...prev, status: "in_progress", priority: "" }));
    } else if (label === "Resolved") {
      setFilters((prev) => ({ ...prev, status: "resolved", priority: "" }));
      setAppliedFilters((prev) => ({ ...prev, status: "resolved", priority: "" }));
    }
    setPage(1);
  };

  const statCards = [
    {
      label: "Total",
      value: stats.total,
      color: "#1D4ED8",
      bg: "#EFF6FF",
      icon: "ticket",
    },
    {
      label: "Open",
      value: stats.open,
      color: "#2563EB",
      bg: "#DBEAFE",
      icon: "info",
    },
    {
      label: "In Progress",
      value: stats.in_progress,
      color: "#C2410C",
      bg: "#FFF7ED",
      icon: "clock",
    },
    {
      label: "Resolved",
      value: stats.resolved,
      color: "#059669",
      bg: "#ECFDF5",
      icon: "check",
    },
  ];

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F8FAFC",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "18px 24px 14px",
          borderBottom: "1px solid #E5E7EB",
          background: "#fff",
        }}
      >
        <div style={{ marginBottom: 14 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "#111827",
            }}
          >
            Tickets
          </h1>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6B7280" }}>
            Track and resolve operational issues
          </p>
        </div>
        {/* Stat cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 10,
            marginBottom: 14,
          }}
        >
          {statCards.map((s) => {
            const isActive = activeCardLabel === s.label;
            const isHovered = hoveredCard === s.label;
            return (
              <div
                key={s.label}
                onMouseEnter={() => setHoveredCard(s.label)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => handleCardClick(s.label)}
                style={{
                  background: s.bg,
                  borderRadius: 8,
                  padding: "10px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  border: `1px solid ${isActive ? s.color : isHovered ? s.color + "99" : s.bg}`,
                  boxShadow: isActive
                    ? `0 0 0 2.5px ${s.color}33, 0 4px 12px rgba(0,0,0,0.08)`
                    : isHovered
                      ? "0 4px 10px rgba(0,0,0,0.05)"
                      : "none",
                  cursor: "pointer",
                  transform: isHovered ? "translateY(-2px)" : "translateY(0)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  }}
                >
                  <Icon name={s.icon} size={15} color={s.color} />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      color: s.color,
                      fontWeight: 600,
                      opacity: 0.75,
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: s.color,
                      lineHeight: 1.2,
                    }}
                  >
                    {s.value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Filters Panel */}
        {hasTickets && (
        <form
          onSubmit={handleSearchSubmit}
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div
            style={{
              position: "relative",
              flex: 1,
              minWidth: 200,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Icon
              name="search"
              size={14}
              color="#9CA3AF"
              style={{ position: "absolute", left: 10, pointerEvents: "none" }}
            />
            <input
              placeholder="Search tickets by title (types instantly)…"
              value={filters.search}
              onChange={(e) =>
                setFilters((f) => ({ ...f, search: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "7px 10px 7px 30px",
                border: "1px solid #E5E7EB",
                borderRadius: 7,
                fontSize: 12,
                outline: "none",
                color: "#111827",
                background: "#fff",
              }}
            />
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            style={{
              padding: "7px 10px",
              border: "1px solid #E5E7EB",
              borderRadius: 7,
              fontSize: 12,
              color: "#374151",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            <option value="">All active statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
            <option value="deleted">Deleted (Archived)</option>
          </select>
          
          <select
            value={filters.priority}
            onChange={(e) => handlePriorityChange(e.target.value)}
            style={{
              padding: "7px 10px",
              border: "1px solid #E5E7EB",
              borderRadius: 7,
              fontSize: 12,
              color: "#374151",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            <option value="">All priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          {hasActiveFilter && (
            <button
              type="button"
              onClick={clearFilters}
              style={{
                padding: "7px 12px",
                border: "1px solid #E5E7EB",
                borderRadius: 7,
                background: "#fff",
                fontSize: 12,
                cursor: "pointer",
                color: "#6B7280",
              }}
            >
              Clear
            </button>
          )}
        </form>
        )}

        {hasTickets && hasActiveFilter && (
          <div
            style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}
          >
            {appliedFilters.status && (
              <span
                style={{
                  fontSize: 11,
                  background: "#EFF6FF",
                  color: "#1D4ED8",
                  padding: "2px 9px",
                  borderRadius: 10,
                  fontWeight: 500,
                }}
              >
                Status: {appliedFilters.status}
              </span>
            )}
            {appliedFilters.priority && (
              <span
                style={{
                  fontSize: 11,
                  background: "#FFF7ED",
                  color: "#C2410C",
                  padding: "2px 9px",
                  borderRadius: 10,
                  fontWeight: 500,
                }}
              >
                Priority: {appliedFilters.priority}
              </span>
            )}
            {appliedFilters.search && (
              <span
                style={{
                  fontSize: 11,
                  background: "#F3F4F6",
                  color: "#374151",
                  padding: "2px 9px",
                  borderRadius: 10,
                  fontWeight: 500,
                }}
              >
                Search: "{appliedFilters.search}"
              </span>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {error ? (
          <ErrorState
            message={error}
            onRetry={fetchTickets}
          />
        ) : loading ? (
          <div
            style={{
              padding: "16px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 7,
            }}
          >
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} height={44} style={{ borderRadius: 7 }} />
            ))}
          </div>
        ) : tickets.length === 0 ? (
          hasTickets ? (
            <EmptyState
              icon="search"
              title="No results found"
              subtitle="No matching items were found for your search query. Try clearing the search or adjusting filters."
            />
          ) : (
            <EmptyState
              icon="ticket"
              title="No tickets found"
              subtitle="No tickets are registered in the system."
            />
          )
        ) : (
          <div className="premium-table-container">
            <table className="premium-table">
              <thead>
                <tr>
                  {[
                    { label: "ID", tip: "Ticket identifier" },
                    {
                      label: "Title & Description",
                      tip: "Ticket title and short description",
                    },
                    { label: "Status", tip: "Current workflow status" },
                    { label: "Priority", tip: "Urgency level" },
                    { label: "Source", tip: "How this ticket was created" },
                    { label: "Created", tip: "Date ticket was opened" },
                  ].map((h) => (
                    <th key={h.label}>
                      <Tooltip text={h.tip}>
                        <span style={{ cursor: "default" }}>
                          {h.label}
                        </span>
                      </Tooltip>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((ticket) => (
                  <tr
                    key={ticket.id}
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <td>
                      <span
                        style={{
                          fontFamily: "monospace",
                          background: "#F1F5F9",
                          border: "1px solid #E2E8F0",
                          padding: "2px 6px",
                          borderRadius: 4,
                          fontSize: 10,
                          color: "#64748B",
                        }}
                      >
                        {ticket.id.slice(0, 8)}…
                      </span>
                    </td>
                    <td style={{ maxWidth: 300 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#0F172A",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {ticket.title}
                      </div>
                      {ticket.ticket_description && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#64748B",
                            marginTop: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ticket.ticket_description}
                        </div>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={ticket.status} />
                    </td>
                    <td>
                      <PriorityBadge priority={ticket.priority} />
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: 11.5,
                          color: "#475569",
                          fontWeight: 500,
                        }}
                      >
                        {ticket.source}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: 11.5, color: "#64748B" }}>
                        {ticket.created_at
                          ? new Date(ticket.created_at).toLocaleString("en-IN")
                          : "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Pagination
        page={page}
        total={totalCount}
        pageSize={PAGE_SIZE}
        onChange={setPage}
      />

      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdated={(updated, options = {}) => {
            if (options.deleted) {
              setTickets((prev) => prev.filter((t) => t.id !== updated.id));
              setSelectedTicket(null);
            } else {
              setTickets((prev) =>
                prev.map((t) => (t.id === updated.id ? updated : t))
              );
              setSelectedTicket(updated);
            }
            fetchTickets();
            fetchStats();
          }}
        />
      )}
    </div>
  );
}
